import { Temporal, parseInstant } from '@shared/lib/date-time';

/**
 * Универсальная функция сортировки массива по полю с датой
 *
 * Сортирует элементы массива по убыванию даты (более свежие первыми).
 * Элементы без даты (null/undefined) помещаются в конец списка.
 *
 * @template T - Тип элементов массива
 * @param array - Массив для сортировки
 * @param dateGetter - Функция для получения значения даты из элемента
 * @returns Новый отсортированный массив (не мутирует оригинал)
 *
 * @example
 * ```typescript
 * const employees = [
 *   { name: 'John', lastActivity: '2024-01-15T12:00:00Z' },
 *   { name: 'Jane', lastActivity: null },
 *   { name: 'Bob', lastActivity: '2024-01-20T12:00:00Z' }
 * ];
 *
 * const sorted = sortByDateField(employees, (emp) => emp.lastActivity);
 * // Результат: [Bob (2024-01-20), John (2024-01-15), Jane (null)]
 * ```
 */
export function sortByDateField<T>(array: T[], dateGetter: (item: T) => string | null | undefined): T[] {
  // Дата разбирается один раз на элемент: внутри comparator это ~log₂(n) разборов на элемент.
  return array
    .map((item) => ({ item, date: parseInstant(dateGetter(item)) }))
    .sort((a, b) => {
      // Элементы без даты идут в конец, сохраняя исходный порядок между собой.
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;

      // Сортируем по убыванию (более новые даты первыми)
      return Temporal.Instant.compare(b.date, a.date);
    })
    .map(({ item }) => item);
}
